import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/** Keeps only the fields the form owns, so read-only extras are never sent back. */
function pickFormFields(record, template) {
  return Object.fromEntries(
    Object.keys(template).map((key) => [key, record[key] ?? template[key]]),
  );
}

function valuesEqual(a, b, keys) {
  return keys.every((key) => a[key] === b[key]);
}

/**
 * Create-or-edit form state for a single record.
 *
 * `blankValues` must be a stable object (define it at module scope) since it
 * doubles as the list of fields the form owns.
 */
export function useRecordForm({ resource, id, blankValues, redirectTo }) {
  const isNew = !id;
  const navigate = useNavigate();
  const fieldKeys = useMemo(() => Object.keys(blankValues), [blankValues]);

  const [values, setValues] = useState(blankValues);
  const [baseline, setBaseline] = useState(blankValues);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isNew) {
      setValues(blankValues);
      setBaseline(blankValues);
      setIsLoading(false);
      return undefined;
    }

    let active = true;
    setIsLoading(true);

    resource
      .retrieve(id)
      .then((record) => {
        if (active) {
          const picked = pickFormFields(record, blankValues);
          setValues(picked);
          setBaseline(picked);
        }
      })
      .catch((apiError) => {
        if (active) {
          setError(apiError.message);
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [resource, id, isNew, blankValues]);

  const isDirty = useMemo(
    () => !valuesEqual(values, baseline, fieldKeys),
    [values, baseline, fieldKeys],
  );

  const setValue = useCallback((name, value) => {
    setValues((previous) => ({ ...previous, [name]: value }));
    // Clear the server-side error for a field as soon as it is edited.
    setFieldErrors((previous) => {
      if (!previous[name]) {
        return previous;
      }
      const { [name]: _removed, ...rest } = previous;
      return rest;
    });
  }, []);

  const handleChange = useCallback(
    (event) => setValue(event.target.name, event.target.value),
    [setValue],
  );

  const discard = useCallback(() => {
    setValues(baseline);
    setFieldErrors({});
    setError(null);
  }, [baseline]);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      setIsSaving(true);
      setError(null);
      setFieldErrors({});

      try {
        if (isNew) {
          await resource.create(values);
          navigate(redirectTo, { replace: true, state: { toastMessage: "Record created successfully." } });
        } else {
          await resource.update(id, values);
          navigate(redirectTo, { replace: true, state: { toastMessage: "Record updated successfully." } });
        }
      } catch (apiError) {
        setError(apiError.fieldErrors ? null : apiError.message);
        setFieldErrors(apiError.fieldErrors ?? {});
      } finally {
        setIsSaving(false);
      }
    },
    [isNew, resource, values, id, navigate, redirectTo],
  );

  const remove = useCallback(async () => {
    setIsSaving(true);
    setError(null);

    try {
      await resource.remove(id);
      navigate(redirectTo, { replace: true });
    } catch (apiError) {
      setError(apiError.message);
    } finally {
      setIsSaving(false);
    }
  }, [resource, id, navigate, redirectTo]);

  return {
    isNew,
    values,
    setValue,
    handleChange,
    isLoading,
    isSaving,
    isDirty,
    discard,
    error,
    fieldErrors,
    submit,
    remove,
  };
}
